export interface AgencyDTO {
  _id: string;
  agency_id: string;
  active?: boolean | null;
  status?: string | null;
  monthly_fee?: number | null;
  monthly_credits?: number | null;
  overage_fee?: number | null;
  allowed_users?: number | null;
  plan_type?: string | null;
  name?: string | null;
  is_self_serviced?: boolean | null;
  /**
   * Owner user's email, joined in by the backend — used to name self-serve
   * signups that never set an agency name (the Agencies record has none).
   */
  owner_email?: string | null;
  credits: number;
  maxCredits: number;
}

export const AGENCY_STATUSES = ["inactive", "active", "trial", "pilot"] as const;
export type AgencyStatus = (typeof AGENCY_STATUSES)[number];

export interface Agency {
  id: string;
  agencyId: string;
  /**
   * Missing `active` counts as active. Edit-only (status dropdown, AgencyPage
   * billing): a missing flag must not read as "inactive", or an unrelated edit
   * persists it and locks the agency out. For list visibility use
   * {@link Agency.isListed}.
   */
  isActive: boolean;
  /**
   * On the onboarding allowlist (`active === true`) — what list UIs filter on,
   * since ~200 prod records have no `active` field and are mostly test data and
   * self-serve signups. Prefer `useAdminGetListedAgencies`; this flag is exposed
   * only to badge a revealed row INACTIVE.
   */
  isListed: boolean;
  /**
   * A self-serve signup that never set an agency name. Grouped under its own
   * collapsible section, not the main grid.
   */
  isSelfServeSignup: boolean;
  status: AgencyStatus | null;
  unknownStatus?: string | null;
  monthlyFee?: number | null;
  monthlyCredits?: number | null;
  overageFee?: number | null;
  allowedUsers?: number | null;
  planType?: string | null;
  name: string;
  credits: number;
  maxCredits: number;
}

export interface RunDTO {
  run_id: string;
  status: string;
  generation_time: string;
  instance_ids: string;
  user: {
    username: string;
    email: string;
  };
  result: {
    carrier: string;
    definitions: object;
    form_schedule: string;
    key_info: string;
    markdown_str: string;
    policy_period: string;
    run_time: number;
    type_vals: object[];
    types: string[];
  };
}

export interface AdminWorkflowDTO {
  workflow_type: string;
  workflow_id: string;
  user_id?: string;
  user_name?: string;
  agency_id?: string;
  agency_name?: string;
  client_id?: string;
  client_name?: string;
  display_name?: string;
  status: string;
  reason?: string | null;
  reason_detail?: string | null;
  generation_time?: string;
  instance_ids?: string[];
  document_count?: number;
}

export interface Run {
  runId: string;
  status: string;
  generationTime: Date;
  instanceIds: string;
  user: {
    username: string;
    email: string;
  };
  result: {
    carrier: string;
    definitions: object;
    formSchedule: string;
    keyInfo: string;
    markdownStr: string;
    policyPeriod: string;
    runTime: number;
    typeVals: object[];
    types: string[];
  };
}

export interface AgencyRunTotal {
  runs: Run[];
}

export interface AgencyUsersInfo {
  total_policies: number;
  total_comparisons: number;
  total_proposals: number;
  total_contract_reviews?: number;
  total_gap_analyses?: number;
  total_single_policy_analyses?: number;
  total_sov?: number;
  total_loss_runs?: number;
  total_chats?: number;
  teams_list: {
    [teamName: string]: {
      team_id: string;
      users_info: {
        user_id: string;
        user_first_name: string;
        user_last_name: string;
        email: string;
        role?: string;
        num_policies: number;
        num_comparisons: number;
        num_proposals: number;
        num_contract_reviews?: number;
        num_gap_analyses?: number;
        num_single_policy_analyses?: number;
        num_sov?: number;
        num_loss_runs?: number;
        num_chats?: number;
        is_admin: boolean;
        is_test_account?: boolean | null;
        last_activity: string;
      }[];
    };
  }[];
}

export interface Dict {
  name: string;
  agencyId: string;
  userCount: number;
  users?: { user_id?: string; email?: string; username?: string }[];
  creationDate?: string | null;
  policiesCount: number;
  comparisonsCount: number;
  proposalsCount: number;
  contractReviewsCount: number;
  gapAnalysisCount: number;
  singlePolicyAnalysisCount: number;
  sovCount: number;
  lossRunsCount: number;
  coiCount: number;
  chatsCount: number;
  policiesCountLastWeek: number;
  comparisonsCountLastWeek: number;
  proposalsCountLastWeek: number;
  contractReviewsCountLastWeek: number;
  gapAnalysisCountLastWeek: number;
  singlePolicyAnalysisCountLastWeek: number;
  sovCountLastWeek: number;
  lossRunsCountLastWeek: number;
  coiCountLastWeek: number;
  chatsCountLastWeek: number;
}

export interface DictDTO {
  name: string;
  agency_id: string;
  user_count: number;
  users?: { user_id?: string; email?: string; username?: string }[];
  created_at?: string | null;
  policies_count: number;
  comparisons_count: number;
  proposals_count: number;
  contract_reviews_count: number;
  gap_analysis_count: number;
  single_policy_analysis_count: number;
  policies_count_last_week: number;
  comparisons_count_last_week: number;
  proposals_count_last_week: number;
  contract_reviews_count_last_week: number;
  gap_analysis_count_last_week: number;
  single_policy_analysis_count_last_week: number;
}

export interface GetAgenciesResponse {
  agencies: AgencyDTO[];
}

export interface GetRunsResponse {
  all_report_runs: RunDTO[];
}

export interface GetUsersInfoResponse {
  total_policies: number;
  total_comparisons: number;
  total_proposals: number;
  total_contract_reviews?: number;
  total_gap_analyses?: number;
  total_single_policy_analyses?: number;
  total_sov?: number;
  total_loss_runs?: number;
  total_chats?: number;
  teams_list: {
    [teamName: string]: {
      team_id: string;
      users_info: {
        user_id: string;
        user_first_name: string;
        user_last_name: string;
        email: string;
        role?: string;
        num_policies: number;
        num_comparisons: number;
        num_proposals: number;
        num_contract_reviews?: number;
        num_gap_analyses?: number;
        num_single_policy_analyses?: number;
        num_sov?: number;
        num_loss_runs?: number;
        num_chats?: number;
        is_admin: boolean;
        is_test_account?: boolean | null;
        last_activity: string;
      }[];
    };
  }[];
}

export interface UserSearchResult {
  user_id: string;
  name: string;
  email: string;
  agency_id: string;
}

export interface UserExportDTO {
  user_id: string;
  name: string;
  email: string;
  username: string;
  is_disabled: boolean;
  agency_name: string | null;
  agency_id: string;
  agency_active: boolean;
  role: string;
  created_at: string | null;
  last_activity_timestamp: string | null;
  policies_count: number;
  comparisons_count: number;
  proposals_count: number;
  contract_reviews_count: number;
  gap_analysis_count: number;
  single_policy_analysis_count: number;
  sov_count: number;
  loss_runs_count: number;
  coi_count: number;
  chats_count: number;
}

export interface DefaultResponse {
  success: boolean;
  message: string;
}

export interface SalesMetricDTO {
  _id: string;
  date: string;
  rep_name: string;
  new_accounts: {
    num_booked: number;
    names: string[];
  };
  account_notes: Array<{ account_name: string; note: string }>;
  pipeline_updates: Array<{ description: string }>;
  created_at: string;
}

export interface GetSalesMetricsResponse {
  metrics: SalesMetricDTO[];
  count: number;
}

export interface GetSalesRepsResponse {
  reps: string[];
}

export interface IssueBucketDTO {
  month: string;
  count: number;
  avg_completion_time_days: number | null;
  issues: IssueDTO[];
}

export interface IssueCategoryDTO {
  name: string;
  description: string;
  reason: string;
  issue_count: number;
  issues: IssueDTO[];
  version: number;
  edited_by: string | null;
  example_issues?: string[];
  calculated_at?: string;
  is_active?: boolean;
  avg_completion_time_days?: number | null;
  buckets?: IssueBucketDTO[];
}

export interface IssueDTO {
  identifier: string;
  title: string;
  url: string;
  category?: string;
  labels?: string[];
  state?: string;
  is_client_reported?: boolean;
  completion_time_days?: number | null;
}

export interface CategoryHistoryEntryDTO {
  version: number;
  calculated_at: string;
  is_active: boolean;
  categories: {
    name: string;
    description: string;
    reason: string;
    issue_count: number;
    edited_by: string | null;
  }[];
}

export interface GetIssueCategoriesResponse {
  categories: IssueCategoryDTO[];
}

export interface GetIssuesResponse {
  issues: IssueDTO[];
  count: number;
}

export interface GetCategoryHistoryResponse {
  history: CategoryHistoryEntryDTO[];
}

export interface CollectIssuesResponse {
  total_fetched: number;
  total_new: number;
  classified: number;
  slotted_into_categories: number;
}

export interface RecalculateCategoriesResponse {
  categories: IssueCategoryDTO[];
  message: string;
}

// ---------------------------------------------------------------------------
// Usage metrics (Internal Metrics Tracking section)
// ---------------------------------------------------------------------------

export type UsageGranularity = "day" | "week" | "month" | "year";
export type UsageGroupBy = "agency" | "user" | "role";

// Mirrored from CATEGORIES in coverflow's services/usage_metrics_service.py.
// Adding a category requires the same id be added there too. See
// docs/USAGE_METRICS_ADDING_CATEGORIES.md in the coverflow repo.
export const USAGE_CATEGORIES = [
  "policies",
  "comparisons",
  "single_policy_analyses",
  "proposals",
  "contract_reviews",
  "gap_analyses",
  "sov",
  "loss_runs",
  "coi",
  "chats",
  // Epic-driven runs are partitioned out of the human categories above so an
  // Epic-driven comparison appears only here, not in `comparisons`. Toggled
  // off by default in the FE — see Toolbar's "Show Epic runs" checkbox.
  "epic_runs",
] as const;
export type UsageCategory = (typeof USAGE_CATEGORIES)[number];

export const USAGE_CATEGORY_LABELS: Record<UsageCategory, string> = {
  policies: "Policy uploads",
  comparisons: "Comparisons",
  single_policy_analyses: "Single policy analyses",
  proposals: "Proposals",
  contract_reviews: "Contract reviews",
  gap_analyses: "Coverage gap analyses",
  sov: "SOV builds",
  loss_runs: "Loss runs",
  coi: "COIs",
  chats: "Chats",
  epic_runs: "Epic runs",
};

export type UsageCategoryCounts = Record<UsageCategory, number>;

export interface UsageBucketDTO {
  start: string;
  label: string;
  totals: UsageCategoryCounts;
  total: number;
}

export interface UsageGroupMetaDTO {
  id: string;
  label: string;
  agency_id?: string;
  user_id?: string;
  email?: string;
  team_id?: string;
  team_name?: string;
  // Free-text role set by the user in their profile. Highly sparse (~20%
  // populated in prod), and a small slice of those are junk values - render
  // whatever's there, but be ready for missing.
  role?: string;
  active?: boolean;
  user_count?: number;
  // Present on agency-grouped timeseries responses. Count of seats with
  // ≥1 workflow event in the window (uploads alone don't count).
  active_user_count?: number;
}

export interface UsageTimeseriesResponseDTO {
  granularity: UsageGranularity;
  start_date: string;
  end_date: string;
  categories: UsageCategory[];
  buckets: UsageBucketDTO[];
  by_group: Record<string, UsageBucketDTO[]>;
  groups: UsageGroupMetaDTO[];
}

export interface UsagePeriodTotalsDTO {
  start?: string;
  end?: string;
  totals: UsageCategoryCounts;
  total: number;
}

export interface UsageDeltaDTO {
  totals: UsageCategoryCounts;
  total: number;
  pct_change: number | null;
  totals_pct_change: Record<UsageCategory, number | null>;
}

export interface UsageGroupComparisonDTO extends UsageGroupMetaDTO {
  period_a: UsagePeriodTotalsDTO;
  period_b: UsagePeriodTotalsDTO;
  delta: UsageDeltaDTO;
  // Per-period engagement counts for agency-grouped responses.
  active_user_count_period_a?: number;
  active_user_count_period_b?: number;
}

export interface UsageComparisonResponseDTO {
  period_a: UsagePeriodTotalsDTO;
  period_b: UsagePeriodTotalsDTO;
  delta: UsageDeltaDTO;
  categories: UsageCategory[];
  groups: UsageGroupComparisonDTO[];
}

export interface UsageTimeseriesParams {
  startDate: string;
  endDate: string;
  granularity: UsageGranularity;
  agencyId?: string | null;
  groupBy?: UsageGroupBy | null;
  includeTestAccounts?: boolean;
}

export interface UsageComparisonParams {
  periodAStart: string;
  periodAEnd: string;
  periodBStart: string;
  periodBEnd: string;
  agencyId?: string | null;
  groupBy?: UsageGroupBy | null;
  includeTestAccounts?: boolean;
}

export interface UsageOverviewParams {
  startDate: string;
  endDate: string;
  includeTestAccounts?: boolean;
}

export interface UsageIterationsParams {
  startDate: string;
  endDate: string;
  agencyId?: string | null;
  includeTestAccounts?: boolean;
}

export interface WorkflowIterationDistribution {
  "1": number;
  "2-3": number;
  "4-5": number;
  "6+": number;
}

export interface WorkflowIterationRowDTO {
  type: string;
  label: string;
  chat_count: number;
  median: number;
  mean: number;
  p90: number;
  one_turn_pct: number;
  distribution: WorkflowIterationDistribution;
}

export interface WorkflowIterationsResponseDTO {
  start_date: string;
  end_date: string;
  by_type: WorkflowIterationRowDTO[];
}

// ---------------------------------------------------------------------------
// Policy insights
// ---------------------------------------------------------------------------

export interface PolicyInsightsParams {
  startDate: string;
  endDate: string;
  agencyId?: string | null;
  includeTestAccounts?: boolean;
}

export interface CarrierRowDTO {
  name: string;
  count: number;
}

export interface CoverageLineRowDTO {
  line: string;
  count: number;
}

export interface LobSplitDTO {
  commercial: number;
  personal: number;
  employee_benefits: number;
  unknown: number;
}

export interface PolicyInsightsResponseDTO {
  start_date: string;
  end_date: string;
  agency_id: string | null;
  carriers: CarrierRowDTO[];
  coverage_lines: CoverageLineRowDTO[];
  lob_split: LobSplitDTO;
  documents_analyzed: number;
  data_points_extracted: number;
}

export interface UsageOverviewResponseDTO {
  start_date: string;
  end_date: string;
  workflow_total: number;
  upload_total: number;
  totals_by_category: UsageCategoryCounts;
  active_user_count: number;
  total_user_count: number;
  active_agency_count: number;
  total_agency_count: number;
}

// ---------------------------------------------------------------------------
// Admin agent
// ---------------------------------------------------------------------------

export type ChartSpec =
  | {
      type: "line" | "bar" | "stacked_bar";
      title?: string;
      subtitle?: string;
      data: Record<string, string | number>[];
      xKey: string;
      yKeys: string[];
    }
  | {
      type: "pie";
      title?: string;
      subtitle?: string;
      data: Record<string, string | number>[];
      nameKey: string;
      valueKey: string;
    }
  | {
      type: "kpi";
      title?: string;
      subtitle?: string;
      value: string | number;
    };

// Anthropic content blocks the agent emits. We only need the three types we
// actually render.
export type AgentContentBlock =
  | { type: "text"; text: string }
  | {
      type: "tool_use";
      id: string;
      name: string;
      input: Record<string, unknown>;
    }
  | {
      type: "tool_result";
      tool_use_id: string;
      // The backend serializes the tool's return value as a JSON string before
      // putting it on the wire, matching Anthropic's tool_result content shape.
      content: string;
    };

export interface AgentMessage {
  role: "user" | "assistant" | "system";
  // user messages are plain strings, assistant messages are blocks.
  content: string | AgentContentBlock[];
}

export interface AgentTurnResponse {
  reply: AgentMessage;
}

export interface ChatSummary {
  chat_id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface ChatDetail extends ChatSummary {
  messages: AgentMessage[];
}

export interface ListChatsResponse {
  chats: ChatSummary[];
}

export interface PrReviewDTO {
  login: string;
  submittedAt: string;
}

export interface PrDTO {
  number: number;
  title: string;
  url: string;
  repo: string;
  author: string;
  isDraft: boolean;
  updatedAt: string;
  reviewDecision: string | null;
  ci: string | null;
  lastCommitAt: string | null;
  reviewRequests: string[];
  reviews: PrReviewDTO[];
  // commenters, reviewers and assignees; missing from copies cached before it was added
  involved?: string[];
}

export interface PrDashboardResponse {
  prs: PrDTO[];
  members: { login: string; name: string | null }[];
  fetchedAt: number;
}
